import React, { createContext, useCallback, useMemo, useState } from "react";
import { NotificationOptions, Notifications } from "../components/notifications";
import { NotificationsContextProviderProps, NotificationsStateSetters } from "./types";

const DEFAULT_NOTIFICATION_OPTIONS: NotificationsContextProviderProps["defaultNotificationOptions"] = {
  variant: "floating",
  inline: false,
};

export const NotificationsStateSetterContext = createContext<NotificationsStateSetters>(undefined);
NotificationsStateSetterContext.displayName = "NotificationsStateSetterContext";

/**
 * A context provider that provides a notifications UI that can be triggered from anywhere within its children sub-tree
 * using convenience functions. These convenience functions can be access via the following companion hook.
 *
 * ### `useNotifications`
 *
 * This hook return the following two functions:
 *
 * - `showNotification`
 * - `dismissNotification`
 *
 * The `showNotification` function takes a `NotificationOptions` object as it's parameter. The properties of this
 * options object cover all props of the `Notification` component thus allowing full customization of the notification
 * content and interactivity.
 *
 * The `dismissNotification` function can be tied to custom application logic to perform useful actions before the
 * notification is dismissed.
 */
export const NotificationsContextProvider = ({
  children,
  defaultNotificationOptions = DEFAULT_NOTIFICATION_OPTIONS,
  ...notificationsPassthroughProps
}: NotificationsContextProviderProps) => {
  const [notifications, setNotifications] = useState<NotificationOptions[]>([]);

  const dismissNotification = useCallback<NotificationsStateSetters["dismissNotification"]>((id) => {
    setNotifications((notifications) => notifications.filter((n) => n.id !== id));
  }, []);

  const showNotification = useCallback<NotificationsStateSetters["showNotification"]>(
    ({ onClose, ...options }) => {
      setNotifications((notifications) => [
        ...notifications,
        {
          ...defaultNotificationOptions,
          onClose: (event, id) => {
            dismissNotification(id);
            onClose?.(event, id);
          },
          ...options,
        },
      ]);
    },
    [defaultNotificationOptions, dismissNotification]
  );

  const stateSetters = useMemo<NotificationsStateSetters>(
    () => ({ showNotification, dismissNotification }),
    [dismissNotification, showNotification]
  );

  return (
    <NotificationsStateSetterContext.Provider value={stateSetters}>
      {children}
      <Notifications notifications={notifications} {...notificationsPassthroughProps} />
    </NotificationsStateSetterContext.Provider>
  );
};
